import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "./api";
import { Loader2, Save, PlugZap, CheckCircle2, XCircle, CircleDashed, Eye, EyeOff } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[#12284B]/20 px-3 py-2 text-sm text-[#12284B] focus:outline-none focus:border-[#E84B1C]";
const btnPrimary = "inline-flex items-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-60";
const btnOutline = "inline-flex items-center gap-2 border border-[#12284B]/20 hover:border-[#E84B1C] text-[#12284B] font-semibold text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-60";

function StatusIndicator({ cfg }) {
  const t = cfg.last_test;
  let icon = <CircleDashed size={16} />, text = "Not configured", cls = "bg-[#12284B]/5 text-[#12284B]/60";
  if (cfg.configured && !t) { text = "Key saved — not tested yet"; cls = "bg-amber-100 text-amber-700"; }
  if (t?.ok) { icon = <CheckCircle2 size={16} />; text = "Connected"; cls = "bg-green-100 text-green-700"; }
  if (t && !t.ok) { icon = <XCircle size={16} />; text = `Connection failed (HTTP ${t.http_status})`; cls = "bg-red-100 text-red-700"; }
  return (
    <span data-testid="cratio-status" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>
      {icon} {text}
    </span>
  );
}

function Notice({ msg }) {
  if (!msg) return null;
  return (
    <div data-testid="cratio-message" className={`mt-4 rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
      {msg.text}
    </div>
  );
}

function SourceCheck({ test, source }) {
  if (!test?.ok) return null;
  return (
    <p data-testid="cratio-source-check" className={`mt-2 text-xs ${test.source_seen ? "text-green-700" : "text-amber-700"}`}>
      {test.source_seen
        ? `Lead source "${source}" exists in your Cratio Lead Source list.`
        : `Lead source "${source}" was not found in your Cratio Lead Source list (${(test.sources_seen || []).join(", ")}).`}
    </p>
  );
}

export function CratioSection() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ api_key: "", enabled: false, source: "Website", status: "New" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);

  const apply = (d) => { setCfg(d); setForm({ api_key: "", enabled: d.enabled, source: d.source, status: d.status }); };
  useEffect(() => { api.get("/admin/integrations/cratio").then((r) => apply(r.data)).catch(() => setMsg({ ok: false, text: "Could not load settings" })); }, []);
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setBusy("save"); setMsg(null);
    try {
      const { data } = await api.put("/admin/integrations/cratio", { ...form, api_key: form.api_key || null });
      apply(data);
      setMsg({ ok: true, text: "Settings saved securely." });
    } catch (err) { setMsg({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) }); }
    setBusy("");
  };

  const test = async () => {
    setBusy("test"); setMsg(null);
    try {
      const { data } = await api.post("/admin/integrations/cratio/test");
      setCfg({ ...cfg, last_test: data });
      setMsg({ ok: data.ok, text: data.ok ? "Connection successful — Cratio accepted the API key." : `Connection failed: ${data.message}` });
    } catch (err) { setMsg({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) }); }
    setBusy("");
  };

  if (!cfg) return msg ? <Notice msg={msg} /> : <Loader2 className="animate-spin text-[#E84B1C]" />;
  return (
    <div className="bg-white rounded-xl border border-[#12284B]/10 p-5 shadow-sm max-w-3xl" data-testid="cratio-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-head font-bold text-lg text-[#12284B]">Cratio CRM</h2>
        <StatusIndicator cfg={cfg} />
      </div>
      <p className="text-sm text-[#12284B]/60 mb-5">Website enquiries are sent to Cratio from the server. The API key is encrypted and never shown again after saving.</p>

      <label className="block text-xs font-semibold text-[#12284B]/70 mb-1">API Key</label>
      <div className="relative">
        <input type={show ? "text" : "password"} autoComplete="new-password" className={`${inputCls} pr-10`} value={form.api_key}
          onChange={(e) => upd("api_key", e.target.value)} data-testid="cratio-api-key-input"
          placeholder={cfg.configured ? `Saved: ${cfg.key_masked} — enter a new key to replace` : "Paste your Cratio CRM API key"} />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#12284B]/50" data-testid="cratio-key-visibility">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      <p className="mt-1 text-xs text-[#12284B]/50">Cratio CRM → Setup → Crm API Key (generated by an administrator).</p>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className="block text-xs font-semibold text-[#12284B]/70 mb-1">Lead Source</label>
          <input className={inputCls} value={form.source} onChange={(e) => upd("source", e.target.value)} data-testid="cratio-source-input" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#12284B]/70 mb-1">Lead Status</label>
          <input className={inputCls} value={form.status} onChange={(e) => upd("status", e.target.value)} data-testid="cratio-lead-status-input" />
        </div>
      </div>
      <SourceCheck test={cfg.last_test} source={cfg.source} />

      <label className="mt-4 flex items-center gap-2 text-sm text-[#12284B] cursor-pointer">
        <input type="checkbox" checked={form.enabled} onChange={(e) => upd("enabled", e.target.checked)} className="accent-[#E84B1C] h-4 w-4" data-testid="cratio-enabled-toggle" />
        Send website enquiries to Cratio (visitors see success only after Cratio confirms)
      </label>

      <div className="flex flex-wrap gap-2 mt-5">
        <button onClick={save} disabled={!!busy} className={btnPrimary} data-testid="cratio-save-btn">
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
        </button>
        <button onClick={test} disabled={!!busy || !cfg.configured} className={btnOutline} data-testid="cratio-test-btn">
          {busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />} Test Connection
        </button>
      </div>
      <Notice msg={msg} />
    </div>
  );
}
